import { typeOrder } from "constants/AppConstants";
import React, { useEffect, useRef, useState } from "react";
import {
  useFormContext,
  UseFormGetValues,
  UseFormRegister,
  UseFormWatch,
} from "react-hook-form";
import { isAgent } from "services/TenantSubmitHandler";
import { TenantPetsData, TenantPetsDocument } from "store/tenant/types";
import { getProfileTitle } from "utils/petFormHelpers";
import { IconName } from "../../components/AnimalCard";
import AnimalProfile from "../../components/AnimalProfile/AnimalProfile";
import { useDeviceType } from "../../hooks/useDeviceType";

type DynamicItem = {
  type: IconName;
  uid: number;
  pet_id?: string | null;
};

export type AgentLandLordProps = {
  agentFomTemplate: any[];
  renderTemplates: (
    templates: any[],
    handlers: {
      register?: UseFormRegister<any>;
      getValues?: UseFormGetValues<any>;
      watch?: UseFormWatch<any>;
      handleAddCard?: (type: IconName) => void;
      handleUploadPetDocuments?: (
        files: FileList,
        petType?: string,
        petIndex?: number | null
      ) => void;
      [key: string]: any;
    },
    animalIndex?: number,
    animalType?: string,
    stageOnePetsData?: TenantPetsData[],
    stageOnePetsDocumentData?: TenantPetsDocument[],
    roleType?: string
  ) => React.ReactNode;
  groupedItems: { [key in IconName]?: DynamicItem[] };
  handleAddCard: (type: IconName) => void;
  handleUploadPetDocuments: (
    files: FileList,
    petType?: string,
    petIndex?: number | null
  ) => void;
  register: UseFormRegister<any>;
  renderSection: (
    sectionKey: string,
    petType?: string,
    petIndex?: number
  ) => React.ReactNode;
  stageOnePetsData: TenantPetsData[];
  stageOnePetsDocumentData: TenantPetsDocument[];
  items: DynamicItem[];
  activeStage?: number;
  handleBulkApproveAll?: () => void;
  handleViewProfile: (
    type: string,
    idx: number,
    petData: any,
    petDocuments: any[]
  ) => void;
  setLandLordStage?: React.Dispatch<React.SetStateAction<number>>;
  landLordStage?: number;
  setCollapseStates?: React.Dispatch<
    React.SetStateAction<{ [key: string]: boolean }>
  >;
  handleAgentDecisionTypeChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  agentDecisionType?: string;
  isQld?: boolean;
};

const AgentLandLord: React.FC<AgentLandLordProps> = ({
  agentFomTemplate,
  renderTemplates,
  handleAddCard,
  register,
  handleUploadPetDocuments,
  renderSection,
  items,
  groupedItems,
  stageOnePetsData,
  stageOnePetsDocumentData,
  activeStage,
  handleBulkApproveAll,
  handleViewProfile,
  landLordStage,
  setLandLordStage,
  setCollapseStates,
  handleAgentDecisionTypeChange,
  agentDecisionType,
  isQld
}) => {
  const formContext = useFormContext();
  const [openProfileKey, setOpenProfileKey] = useState<string | null>(null);
  const profileRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const isMobile = useDeviceType();

  /**
   * Effect: Open the first profile by default when items change.
   * Sets openProfileKey to the first item's key if items exist.
   */
  useEffect(() => {
    if (items && items.length > 0) {
      // setOpenProfileKey(`${items[0].type}-${items[0].uid}`); // If first item is open by default enable this code
    }
  }, [items]);

  if (!formContext) {
    return null; // or render a fallback UI
  }
  const { getValues, watch } = formContext;

  // const agentDecisionType = watch("agent_decision_type");
 
  const renderAnimalProfiles = (withLandlordSection: boolean) => (
    <div className="d-flex flex-column gap-20px">
      {withLandlordSection && renderSection("landlord_response_section")}
      {renderSection("bulk_approve")}
      {items?.length > 0 && (
        <div className="d-flex flex-column gap-20px text-black">
          {typeOrder.map((type) =>
            groupedItems[type]?.map((item, idx) => {
              const petData = stageOnePetsData?.find(
                (pet: any) => pet.id === item?.pet_id
              );
              const petDocumentData = petData?.support_documents;
              const allValues = getValues();
              const animalType = type;
              const consent = allValues?.[animalType]?.agent_consent?.[idx];
              const condition = allValues?.[animalType]?.agent_consent_conditions?.[idx];
              let approvalStatus, conditionStatus;
              if (consent === "i_consent") {
                approvalStatus = "approved";
                if (condition !== "" && condition !== undefined && condition !== null) {
                  conditionStatus = condition === "i_consent_no_conditions" ? "no-condition" : "condition-apply";
                }
              } else if (consent === "i_don_not_consent") {
                approvalStatus = "declined";
                conditionStatus = undefined;
              } else {
                approvalStatus = "incomplete";
                conditionStatus = undefined;
              }
              const profileTitle = getProfileTitle(type, idx);
              return (
                <div
                  key={item.uid}
                  ref={(el) => { profileRefs.current[`${type}-${item.uid}`] = el; }}
                >
                  <AnimalProfile
                    petId={item.pet_id}
                    key={item.uid + item.type}
                    type={item.type}
                    title={profileTitle}
                    onRemove={() => {}}
                    animalType={type}
                    petData={petData ? [petData] : undefined}
                    petDocumentData={petDocumentData ? petDocumentData : undefined}
                    activeState={activeStage}
                    approvalStatus={approvalStatus}
                    conditionStatus={conditionStatus}
                    roleType="agent"
                    handleViewProfile={() => handleViewProfile(type, idx, petData ?? null, petDocumentData || [])}
                    collapse={openProfileKey !== `${type}-${item.uid}`}
                    onOpen={() => {
                      const key = `${type}-${item.uid}`;
                      const isCurrentlyOpen = openProfileKey === key;
                      setOpenProfileKey((prev) => (prev === key ? null : key));
                      if (!isCurrentlyOpen) {
                        setTimeout(() => {
                          const ref = profileRefs.current[key];
                          if (ref) ref.scrollIntoView({ behavior: "smooth", block: "start" });
                        }, 300);
                      }
                    }}
                  >
                    {renderSection("approve_pets", type, idx)}
                  </AnimalProfile>
                </div>
              );
            })
          )}
        </div>
      )}
      {renderSection("tenant_conditions")}
      {renderSection("agent_general_consent")}
    </div>
  );

  // Mobile rendering logic
  if (isMobile === "mobile" && isAgent()) {
    if (isQld) {
      return (
        <>
          {activeStage === 2 && renderAnimalProfiles(false)}
        </>
      );
    }

    return (
      <>
        {activeStage === 1 && renderSection("agent_decision_type_section")}
        {activeStage === 1 &&
          agentFomTemplate &&
          agentFomTemplate.length > 0 &&
          renderTemplates(agentFomTemplate, {
            handleAddCard,
            watch,
            register,
            getValues,
            handleUploadPetDocuments,
            handleAgentDecisionTypeChange
          })}
        {/* {activeStage === 2 && renderSection("agent_decision_type_section")} */}
        {agentDecisionType === "i_will_complete_then_request_sign" && activeStage === 2 && (
          renderAnimalProfiles(true)
        )}
      </>
    );
  }



  // Desktop rendering: show all sections, no Next button
  if (isQld) {
    return <>{renderAnimalProfiles(false)}</>;
  }

  return (
    <>
      {renderSection("agent_decision_type_section")}
      {agentFomTemplate &&
        agentFomTemplate.length > 0 &&
        renderTemplates(agentFomTemplate, {
          handleAddCard,
          register,
          watch,
          getValues,
          handleUploadPetDocuments,
          handleAgentDecisionTypeChange
        })}

        

      {agentDecisionType === "i_will_complete_then_request_sign" && (
        renderAnimalProfiles(true)
      )}
    </>
  );
};

export default AgentLandLord;
